// src/pages/NetworkTopology.jsx
// Palo Alto-style Dynamic Network Topology Visualization
// With Edge Node filter dropdown and lighter color scheme

import React, { useState, useEffect, useCallback } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Panel,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { edgeNodesService } from '../services/edge-nodes';
import { eventsService } from '../services/events';

// ════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ════════════════════════════════════════════════════════════════════════════

// Determine if an edge node is online based on last heartbeat (30 min threshold)
// Uses UTC comparison to be timezone-safe across different client locations
const isNodeOnline = (node) => {
  if (!node.last_heartbeat) return false;
  
  // Ensure timestamp is parsed as UTC
  let heartbeatStr = node.last_heartbeat;
  if (!heartbeatStr.endsWith('Z') && !heartbeatStr.includes('+')) {
    heartbeatStr += 'Z';  // Treat as UTC if no timezone specified
  }
  
  const lastHeartbeat = new Date(heartbeatStr).getTime();
  const nowUTC = Date.now();  // Always UTC milliseconds
  const diffMinutes = (nowUTC - lastHeartbeat) / (1000 * 60);
  
  return diffMinutes <= 30;
};

// Get node status string
const getNodeStatus = (node) => {
  if (!node) return 'unknown';
  return node.status || 'unknown';
};

// ════════════════════════════════════════════════════════════════════════════
// CUSTOM NODE COMPONENTS - LIGHTER THEME
// ════════════════════════════════════════════════════════════════════════════

// Cloud/Management Plane Node
const CloudNode = ({ data }) => (
  <div className="relative group">
    {/* Connection handle - bottom */}
    <Handle
      type="source"
      position={Position.Bottom}
      className="!w-3 !h-3 !bg-cyan-400 !border-2 !border-white"
    />
    
    {/* Outer glow ring */}
    <div className="absolute -inset-2 bg-gradient-to-r from-cyan-400/30 via-blue-400/30 to-purple-400/30 rounded-2xl blur-xl opacity-75 group-hover:opacity-100 transition-opacity duration-500" />
    
    <div className="relative bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 border-2 border-cyan-400/50 rounded-2xl p-6 min-w-[200px] shadow-2xl shadow-cyan-500/20">
      {/* Status indicator */}
      <div className="absolute -top-1.5 -right-1.5 w-4 h-4">
        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
        <span className="relative inline-flex rounded-full h-4 w-4 bg-emerald-400 shadow-lg shadow-emerald-400/50" />
      </div>
      
      {/* Icon */}
      <div className="flex items-center justify-center mb-3">
        <div className="w-14 h-14 bg-gradient-to-br from-cyan-400/30 to-blue-500/30 rounded-xl flex items-center justify-center border border-cyan-400/50">
          <svg className="w-8 h-8 text-cyan-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
          </svg>
        </div>
      </div>
      
      {/* Label */}
      <div className="text-center">
        <div className="text-sm font-semibold text-white tracking-wide">{data.label}</div>
        <div className="text-xs text-slate-300 mt-1 font-mono">{data.sublabel}</div>
      </div>
      
      {/* Stats bar */}
      <div className="mt-4 pt-3 border-t border-slate-600/50 flex justify-center gap-4">
        <div className="text-center">
          <div className="text-lg font-bold text-emerald-300">{data.edgeCount || 0}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Agents</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-blue-300">{data.assetCount || 0}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Devices</div>
        </div>
      </div>
    </div>
  </div>
);

// Edge Node/Gateway Node - LIGHTER
const EdgeGatewayNode = ({ data }) => {
  const isOnline = data.status === 'Online';
  
  return (
    <div className="relative group cursor-pointer">
      {/* Connection handle - top (from cloud) */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !bg-emerald-400 !border-2 !border-white"
      />
      {/* Connection handle - bottom (to devices) */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !bg-blue-400 !border-2 !border-white"
      />
      
      {/* Glow effect */}
      <div className={`absolute -inset-1.5 bg-gradient-to-r ${isOnline ? 'from-emerald-400/30 to-teal-400/30' : 'from-red-400/30 to-orange-400/30'} rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-all duration-300`} />
      
      <div className={`relative bg-gradient-to-br from-slate-600 to-slate-700 border-2 ${isOnline ? 'border-emerald-400/60 hover:border-emerald-300' : 'border-red-400/60 hover:border-red-300'} rounded-xl p-4 min-w-[220px] shadow-xl transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl`}>
        {/* Status dot */}
        <div className="absolute -top-1 -right-1 w-3 h-3">
          {isOnline && <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />}
          <span className={`relative inline-flex rounded-full h-3 w-3 ${isOnline ? 'bg-emerald-400 shadow-emerald-400/50' : 'bg-red-400 shadow-red-400/50'} shadow-lg`} />
        </div>
        
        {/* Content */}
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isOnline ? 'bg-emerald-500/30' : 'bg-red-500/30'}`}>
            <svg className={`w-5 h-5 ${isOnline ? 'text-emerald-300' : 'text-red-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            {/* Primary: Edge Node ID */}
            <div className="text-sm font-semibold text-white truncate">{data.nodeId}</div>
            {/* Secondary: Hostname */}
            {data.hostname && data.hostname !== data.nodeId && (
              <div className="text-[10px] text-slate-400 font-mono truncate">{data.hostname}</div>
            )}
          </div>
        </div>
        
        {/* Stats row */}
        <div className="mt-3 pt-2 border-t border-slate-500/30 flex justify-between text-xs">
          <div>
            <span className="text-slate-400">Status</span>
            <div className={`font-semibold ${isOnline ? 'text-emerald-300' : 'text-red-300'}`}>{data.status}</div>
          </div>
          <div className="text-right">
            <span className="text-slate-400">Devices</span>
            <div className="font-semibold text-blue-300">{data.deviceCount || 0}</div>
          </div>
        </div>
        
        {/* IP Address if available */}
        {data.ipAddress && (
          <div className="mt-2 text-center">
            <span className="text-[10px] px-2 py-0.5 bg-slate-800/50 rounded-full text-cyan-300 font-mono">
              {data.ipAddress}
            </span>
          </div>
        )}
        
        {/* Version tag */}
        {data.version && (
          <div className="mt-2 text-center">
            <span className="text-[9px] px-2 py-0.5 bg-slate-800/50 rounded-full text-slate-400 font-mono">
              v{data.version}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// Discovered Device Node (PLC, OPC-UA Server, etc.) - LIGHTER
const DeviceNode = ({ data }) => {
  const protocolColors = {
    modbus: { bg: 'from-orange-400/30 to-amber-500/30', border: 'border-orange-400/60', text: 'text-orange-300', badge: 'bg-orange-500/30 text-orange-200 border-orange-400/50' },
    opcua: { bg: 'from-purple-400/30 to-violet-500/30', border: 'border-purple-400/60', text: 'text-purple-300', badge: 'bg-purple-500/30 text-purple-200 border-purple-400/50' },
    tcp: { bg: 'from-blue-400/30 to-cyan-500/30', border: 'border-blue-400/60', text: 'text-blue-300', badge: 'bg-blue-500/30 text-blue-200 border-blue-400/50' },
    s7: { bg: 'from-green-400/30 to-emerald-500/30', border: 'border-green-400/60', text: 'text-green-300', badge: 'bg-green-500/30 text-green-200 border-green-400/50' },
    default: { bg: 'from-slate-400/30 to-gray-500/30', border: 'border-slate-400/60', text: 'text-slate-300', badge: 'bg-slate-500/30 text-slate-200 border-slate-400/50' },
  };
  
  const primaryProtocol = data.protocols?.[0] || 'default';
  const colors = protocolColors[primaryProtocol] || protocolColors.default;
  
  // Determine if high risk based on policy violations
  const hasViolations = data.policyViolations > 0;
  const riskBorder = hasViolations ? 'border-red-400 hover:border-red-300' : colors.border;

  return (
    <div className="relative group cursor-pointer">
      {/* Connection handle - top (from edge node) */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !bg-orange-400 !border-2 !border-white"
      />
      
      {/* Alert pulse for devices with policy violations */}
      {hasViolations && (
        <div className="absolute -inset-2 bg-gradient-to-r from-red-400/40 to-rose-400/40 rounded-xl blur-lg opacity-60 animate-pulse" />
      )}
      <div className={`absolute -inset-1 bg-gradient-to-r ${colors.bg} rounded-lg blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
      
      <div className={`relative bg-gradient-to-br from-slate-600 to-slate-700 border-2 ${riskBorder} rounded-lg p-3 min-w-[160px] shadow-lg transition-all duration-300 group-hover:scale-105`}>
        {/* Alert badge */}
        {hasViolations && (
          <div className="absolute -top-2 -right-2 px-2 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full shadow-lg animate-pulse">
            {data.policyViolations} alerts
          </div>
        )}
        
        {/* Device icon and role */}
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br ${colors.bg}`}>
            <svg className={`w-4 h-4 ${colors.text}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
            </svg>
          </div>
          <div>
            <div className="text-xs font-semibold text-white">{data.role || 'Device'}</div>
            <div className="text-[9px] text-slate-300">DISCOVERED</div>
          </div>
        </div>
        
        {/* IP Address */}
        <div className="bg-slate-800/60 rounded px-2 py-1.5 mb-2">
          <div className={`text-sm font-mono font-semibold ${hasViolations ? 'text-red-300' : 'text-cyan-300'}`}>
            {data.ip}
          </div>
        </div>
        
        {/* Protocol badges */}
        {data.protocols && data.protocols.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {data.protocols.map((protocol, idx) => {
              const pColor = protocolColors[protocol] || protocolColors.default;
              return (
                <span key={idx} className={`text-[9px] px-1.5 py-0.5 rounded border font-semibold uppercase ${pColor.badge}`}>
                  {protocol}
                </span>
              );
            })}
          </div>
        )}
        
        {/* Event count */}
        <div className="text-[10px] text-slate-300 text-center">
          {data.eventCount || 0} events
        </div>
      </div>
    </div>
  );
};

// Node types registry
const nodeTypes = {
  cloud: CloudNode,
  edgeGateway: EdgeGatewayNode,
  device: DeviceNode,
};

// ════════════════════════════════════════════════════════════════════════════
// EDGE NODE FILTER DROPDOWN
// ════════════════════════════════════════════════════════════════════════════

const EdgeNodeFilter = ({ edgeNodes, selectedAgent, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const selectedNode = selectedAgent 
    ? edgeNodes.find(n => n.edge_node_id === selectedAgent)
    : null;
  
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 bg-slate-700/80 hover:bg-slate-600/80 border border-slate-500/50 rounded-lg px-4 py-2 text-sm text-white transition-colors min-w-[280px]"
      >
        <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
        </svg>
        <span className="flex-1 text-left truncate">
          {selectedNode ? selectedNode.edge_node_id : 'All Edge Nodes'}
        </span>
        <svg className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 mt-1 w-full bg-slate-800 border border-slate-600/50 rounded-lg shadow-xl z-50 overflow-hidden">
            <div className="max-h-80 overflow-y-auto">
              {/* All option */}
              <button
                onClick={() => { onSelect(null); setIsOpen(false); }}
                className={`w-full px-4 py-3 text-left text-sm hover:bg-slate-700/50 transition-colors flex items-center gap-2 ${
                  !selectedAgent ? 'bg-cyan-500/20 text-cyan-300' : 'text-slate-200'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                All Edge Nodes
              </button>
              
              {/* Individual agents - sorted by edge_node_id */}
              {[...edgeNodes]
                .sort((a, b) => (a.edge_node_id || '').localeCompare(b.edge_node_id || ''))
                .map(node => {
                  const status = getNodeStatus(node);
                  const isOnline = status === 'Online';
                  return (
                <button
                  key={node.edge_node_id}
                  onClick={() => { onSelect(node.edge_node_id); setIsOpen(false); }}
                  className={`w-full px-4 py-3 text-left hover:bg-slate-700/50 transition-colors ${
                    selectedAgent === node.edge_node_id ? 'bg-cyan-500/20' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isOnline ? 'bg-emerald-400' : 'bg-red-400'}`} />
                    <div className="min-w-0 flex-1">
                      {/* Primary: Edge Node ID */}
                      <div className={`text-sm font-medium truncate ${
                        selectedAgent === node.edge_node_id ? 'text-cyan-300' : 'text-white'
                      }`}>
                        {node.edge_node_id}
                      </div>
                      {/* Secondary: Hostname */}
                      {node.hostname && (
                        <div className="text-[11px] text-slate-400 truncate mt-0.5">
                          {node.hostname}
                        </div>
                      )}
                    </div>
                    {/* Status badge */}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                      isOnline 
                        ? 'bg-emerald-500/20 text-emerald-300' 
                        : 'bg-red-500/20 text-red-300'
                    }`}>
                      {status}
                    </span>
                  </div>
                </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// DETAIL PANEL COMPONENT - LIGHTER
// ════════════════════════════════════════════════════════════════════════════

const DetailPanel = ({ selectedNode, onClose }) => {
  if (!selectedNode) return null;
  
  return (
    <div className="absolute top-4 right-4 w-80 bg-slate-800/95 backdrop-blur-xl border border-slate-600/50 rounded-2xl shadow-2xl overflow-hidden z-50">
      <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 px-4 py-3 border-b border-slate-600/50 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">
          {selectedNode.type === 'edgeGateway' ? 'Edge Agent Details' : 
           selectedNode.type === 'device' ? 'Discovered Device' : 
           selectedNode.type === 'cloud' ? 'Management Plane' : 'Details'}
        </h3>
        <button onClick={onClose} className="text-slate-300 hover:text-white transition-colors">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      <div className="p-4 space-y-4 max-h-[500px] overflow-y-auto">
        {selectedNode.type === 'cloud' && (
          <>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-cyan-500/30 border border-cyan-400/50">
                <svg className="w-6 h-6 text-cyan-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                </svg>
              </div>
              <div>
                <div className="text-lg font-semibold text-white">{selectedNode.data.label}</div>
                <div className="text-xs text-slate-300">{selectedNode.data.sublabel}</div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-700/50 rounded-lg p-3">
                <div className="text-[10px] text-slate-400 uppercase mb-1">Agents</div>
                <div className="text-xl font-bold text-emerald-300">{selectedNode.data.edgeCount || 0}</div>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-3">
                <div className="text-[10px] text-slate-400 uppercase mb-1">Devices</div>
                <div className="text-xl font-bold text-blue-300">{selectedNode.data.assetCount || 0}</div>
              </div>
            </div>
            
            <div className="bg-emerald-500/20 border border-emerald-400/30 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-sm text-emerald-300">Cloud Connected</span>
              </div>
              <div className="text-xs text-slate-400 mt-1">Management plane operational</div>
            </div>
          </>
        )}
        
        {selectedNode.type === 'edgeGateway' && (
          <>
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                selectedNode.data.status === 'Online' 
                  ? 'bg-emerald-500/30 border border-emerald-400/50' 
                  : 'bg-red-500/30 border border-red-400/50'
              }`}>
                <svg className={`w-6 h-6 ${selectedNode.data.status === 'Online' ? 'text-emerald-300' : 'text-red-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
                </svg>
              </div>
              <div>
                {/* Primary: Edge Node ID */}
                <div className="text-lg font-semibold text-white">{selectedNode.data.nodeId}</div>
                {/* Secondary: Hostname */}
                {selectedNode.data.hostname && selectedNode.data.hostname !== selectedNode.data.nodeId && (
                  <div className="text-xs text-slate-400">{selectedNode.data.hostname}</div>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-700/50 rounded-lg p-2">
                <div className="text-[10px] text-slate-400 uppercase">Status</div>
                <div className={`text-sm font-semibold ${selectedNode.data.status === 'Online' ? 'text-emerald-300' : 'text-red-300'}`}>
                  {selectedNode.data.status}
                </div>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-2">
                <div className="text-[10px] text-slate-400 uppercase">Devices</div>
                <div className="text-sm font-semibold text-blue-300">{selectedNode.data.deviceCount || 0}</div>
              </div>
            </div>
            
            {selectedNode.data.ipAddress && (
              <div className="bg-slate-700/50 rounded-lg p-3">
                <div className="text-[10px] text-slate-400 uppercase mb-1">IP Address</div>
                <div className="text-sm text-cyan-300 font-mono">{selectedNode.data.ipAddress}</div>
              </div>
            )}
            
            {selectedNode.data.lastHeartbeat && (
              <div className="bg-slate-700/50 rounded-lg p-3">
                <div className="text-[10px] text-slate-400 uppercase mb-1">Last Heartbeat</div>
                <div className="text-sm text-slate-200">
                  {new Date(selectedNode.data.lastHeartbeat).toLocaleString('en-US', {
                    timeZone: 'America/Chicago',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                  })}
                </div>
              </div>
            )}
            
            {selectedNode.data.version && (
              <div className="bg-slate-700/50 rounded-lg p-3">
                <div className="text-[10px] text-slate-400 uppercase mb-1">Agent Version</div>
                <div className="text-sm text-slate-200 font-mono">v{selectedNode.data.version}</div>
              </div>
            )}
          </>
        )}
        
        {selectedNode.type === 'device' && (
          <>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-blue-500/30 border border-blue-400/50">
                <svg className="w-6 h-6 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
              </div>
              <div>
                <div className="text-lg font-semibold text-white">{selectedNode.data.role || 'Device'}</div>
                <div className="text-xs text-slate-300">Discovered via scanning</div>
              </div>
            </div>
            
            <div className="bg-slate-700/50 rounded-lg p-3">
              <div className="text-[10px] text-slate-400 uppercase mb-1">IP Address</div>
              <div className="text-lg text-cyan-300 font-mono font-semibold">{selectedNode.data.ip}</div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-700/50 rounded-lg p-2">
                <div className="text-[10px] text-slate-400 uppercase">Events</div>
                <div className="text-sm font-semibold text-blue-300">{selectedNode.data.eventCount || 0}</div>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-2">
                <div className="text-[10px] text-slate-400 uppercase">Alerts</div>
                <div className={`text-sm font-semibold ${selectedNode.data.policyViolations > 0 ? 'text-red-300' : 'text-slate-300'}`}>
                  {selectedNode.data.policyViolations || 0}
                </div>
              </div>
            </div>
            
            {selectedNode.data.protocols && selectedNode.data.protocols.length > 0 && (
              <div className="bg-slate-700/50 rounded-lg p-3">
                <div className="text-[10px] text-slate-400 uppercase mb-2">Protocols Detected</div>
                <div className="flex flex-wrap gap-2">
                  {selectedNode.data.protocols.map((protocol, idx) => (
                    <span key={idx} className="text-xs px-2 py-1 bg-blue-500/30 text-blue-200 rounded border border-blue-400/50 uppercase font-semibold">
                      {protocol}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {selectedNode.data.policies && selectedNode.data.policies.length > 0 && (
              <div className="bg-slate-700/50 rounded-lg p-3">
                <div className="text-[10px] text-slate-400 uppercase mb-2">Policy Matches</div>
                <div className="space-y-1">
                  {selectedNode.data.policies.slice(0, 5).map((policy, idx) => (
                    <div key={idx} className="text-xs text-slate-200 bg-slate-600/50 rounded px-2 py-1">
                      {policy}
                    </div>
                  ))}
                  {selectedNode.data.policies.length > 5 && (
                    <div className="text-xs text-slate-400">+{selectedNode.data.policies.length - 5} more</div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// LEGEND COMPONENT - LIGHTER
// ════════════════════════════════════════════════════════════════════════════

const Legend = () => (
  <div className="bg-slate-800/90 backdrop-blur-sm border border-slate-600/50 rounded-xl p-4 space-y-3">
    <div className="text-xs font-semibold text-slate-200 uppercase tracking-wider">Legend</div>
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-full bg-emerald-400" />
        <span className="text-xs text-slate-300">Agent Online</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-full bg-red-400" />
        <span className="text-xs text-slate-300">Agent Offline / Alerts</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded bg-orange-400/70" />
        <span className="text-xs text-slate-300">Modbus Device</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded bg-purple-400/70" />
        <span className="text-xs text-slate-300">OPC-UA Device</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-8 h-0.5 bg-gradient-to-r from-cyan-400 to-cyan-400/30" />
        <span className="text-xs text-slate-300">Data Flow</span>
      </div>
    </div>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// STATS BAR COMPONENT
// ════════════════════════════════════════════════════════════════════════════

const StatsBar = ({ stats }) => (
  <div className="flex items-center gap-4">
    <div className="flex items-center gap-2 bg-slate-700/50 rounded-lg px-3 py-2 border border-slate-600/30">
      <span className="text-emerald-300 font-bold text-lg">{stats.onlineAgents}</span>
      <span className="text-slate-300 text-xs">/ {stats.totalAgents} Online</span>
    </div>
    <div className="flex items-center gap-2 bg-slate-700/50 rounded-lg px-3 py-2 border border-slate-600/30">
      <span className="text-blue-300 font-bold text-lg">{stats.totalDevices}</span>
      <span className="text-slate-300 text-xs">Devices</span>
    </div>
    {stats.alertCount > 0 && (
      <div className="flex items-center gap-2 bg-red-500/20 rounded-lg px-3 py-2 border border-red-400/50 animate-pulse">
        <span className="text-red-300 font-bold text-lg">{stats.alertCount}</span>
        <span className="text-red-300/70 text-xs">Alerts</span>
      </div>
    )}
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════

const NetworkTopology = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [edgeNodesList, setEdgeNodesList] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [allEvents, setAllEvents] = useState([]);
  const [stats, setStats] = useState({
    totalAgents: 0,
    onlineAgents: 0,
    totalDevices: 0,
    alertCount: 0,
  });

  // Build topology from Edge Nodes + Events (filtered by selected agent)
  const buildTopology = useCallback((edgeNodes, events, filterAgentId = null) => {
    const newNodes = [];
    const newEdges = [];
    
    // Filter edge nodes if agent is selected
    const filteredEdgeNodes = filterAgentId 
      ? edgeNodes.filter(n => n.edge_node_id === filterAgentId)
      : edgeNodes;
    
    // Layout constants - simpler when filtering to single agent
    const CLOUD_Y = 0;
    const EDGE_Y = 200;
    const DEVICE_Y_START = 420;
    const HORIZONTAL_GAP = filteredEdgeNodes.length > 4 ? 260 : filteredEdgeNodes.length > 3 ? 300 : 350;
    const DEVICE_GAP = 180;
    
    // Group events by edge node and destination IP
    const devicesByAgent = {};
    let totalAlerts = 0;
    
    events.forEach(event => {
      const agentId = event.celerway_edge_node_id;
      const destIp = event.destination_ip;
      
      if (!agentId || !destIp) return;
      
      // Skip if filtering and this isn't the selected agent
      if (filterAgentId && agentId !== filterAgentId) return;
      
      if (!devicesByAgent[agentId]) {
        devicesByAgent[agentId] = {};
      }
      
      if (!devicesByAgent[agentId][destIp]) {
        devicesByAgent[agentId][destIp] = {
          ip: destIp,
          protocols: new Set(),
          eventCount: 0,
          policyViolations: 0,
          policies: new Set(),
          role: 'PLC',
        };
      }
      
      const device = devicesByAgent[agentId][destIp];
      device.eventCount++;
      
      if (event.protocol) {
        device.protocols.add(event.protocol.toLowerCase());
      }
      
      if (event.details?.policy_name) {
        device.policies.add(event.details.policy_name);
        if (event.severity === 'Warning' || event.severity === 'Critical' || event.severity === 'High') {
          device.policyViolations++;
          totalAlerts++;
        }
      }
      
      if (device.protocols.has('modbus') || device.protocols.has('opcua')) {
        device.role = 'PLC';
      }
    });
    
    // Count unique devices
    let totalDevices = 0;
    Object.values(devicesByAgent).forEach(devices => {
      totalDevices += Object.keys(devices).length;
    });
    
    // Calculate center X based on edge nodes spread
    const edgeStartX = 400 - ((filteredEdgeNodes.length - 1) * HORIZONTAL_GAP) / 2;
    const centerX = edgeStartX + ((filteredEdgeNodes.length - 1) * HORIZONTAL_GAP) / 2;
    
    // 1. Add Cloud node (centered above edge nodes)
    newNodes.push({
      id: 'cloud',
      type: 'cloud',
      position: { x: centerX, y: CLOUD_Y },
      data: {
        label: 'AI Sentry Cloud',
        sublabel: 'Management Plane',
        edgeCount: filteredEdgeNodes.length,
        assetCount: totalDevices,
      },
    });
    
    // 2. Add Edge Nodes
    filteredEdgeNodes.forEach((node, idx) => {
      const agentDevices = devicesByAgent[node.edge_node_id] || {};
      const deviceList = Object.values(agentDevices);
      const nodeX = edgeStartX + idx * HORIZONTAL_GAP;
      
      const agentIp = events.find(e => 
        e.celerway_edge_node_id === node.edge_node_id && e.source_ip
      )?.source_ip || null;
      
      // Calculate status from heartbeat (30 min threshold)
      const status = getNodeStatus(node);
      
      newNodes.push({
        id: `edge-${node.edge_node_id}`,
        type: 'edgeGateway',
        position: { x: nodeX, y: EDGE_Y },
        data: {
          label: node.edge_node_id,  // Primary display name
          nodeId: node.edge_node_id,
          status: status,  // Calculated from heartbeat
          deviceCount: deviceList.length,
          version: node.ot_sentry_agent_version,
          hostname: node.hostname,  // Secondary/subordinate
          ipAddress: agentIp,
          lastHeartbeat: node.last_heartbeat,
        },
      });
      
      // Edge: Cloud → Edge Node
      newEdges.push({
        id: `cloud-to-edge-${node.edge_node_id}`,
        source: 'cloud',
        target: `edge-${node.edge_node_id}`,
        type: 'smoothstep',
        animated: true,
        style: { 
          stroke: status === 'Online' ? '#34d399' : '#f87171',
          strokeWidth: 2,
        },
      });
      
      // 3. Add discovered devices for this agent
      const deviceStartX = nodeX - ((deviceList.length - 1) * DEVICE_GAP) / 2;
      
      deviceList.forEach((device, deviceIdx) => {
        const deviceId = `device-${node.edge_node_id}-${device.ip.replace(/\./g, '-')}`;
        
        newNodes.push({
          id: deviceId,
          type: 'device',
          position: { 
            x: deviceStartX + deviceIdx * DEVICE_GAP, 
            y: DEVICE_Y_START + (deviceIdx % 2) * 60
          },
          data: {
            ip: device.ip,
            role: device.role,
            protocols: Array.from(device.protocols),
            eventCount: device.eventCount,
            policyViolations: device.policyViolations,
            policies: Array.from(device.policies),
          },
        });
        
        // Edge: Edge Node → Device
        const hasAlerts = device.policyViolations > 0;
        const edgeColor = hasAlerts ? '#f87171' : 
                   device.protocols.has('modbus') ? '#fb923c' :
                   device.protocols.has('opcua') ? '#c084fc' : '#22d3ee';
        
        newEdges.push({
          id: `edge-${node.edge_node_id}-to-${deviceId}`,
          source: `edge-${node.edge_node_id}`,
          target: deviceId,
          type: 'smoothstep',
          animated: true,
          style: { 
            stroke: edgeColor,
            strokeWidth: 2,
          },
        });
      });
    });
    
    return { 
      nodes: newNodes, 
      edges: newEdges, 
      stats: {
        totalAgents: filteredEdgeNodes.length,
        onlineAgents: filteredEdgeNodes.filter(n => isNodeOnline(n)).length,
        totalDevices,
        alertCount: totalAlerts,
      }
    };
  }, []);

  // Fetch data
  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [edgeNodesResponse, eventsResponse] = await Promise.all([
        edgeNodesService.getEdgeNodes(),
        eventsService.getEvents({ limit: 500 }),
      ]);
      
      const edgeNodes = edgeNodesResponse.data || edgeNodesResponse || [];
      const events = eventsResponse.data || eventsResponse || [];
      
      setEdgeNodesList(edgeNodes);
      setAllEvents(events);
      
      // Only set initial agent on first load (when selectedAgent is null)
      // Don't reset it on subsequent refreshes
      const agentToUse = selectedAgent || (edgeNodes.length > 0 ? edgeNodes[4].edge_node_id : null);
      
      if (!selectedAgent && edgeNodes.length > 0) {
        console.log('🔍 Initial agent selection:', agentToUse);
        setSelectedAgent(agentToUse);
      } else {
        console.log('🔍 Keeping selected agent:', selectedAgent);
      }
      
      const { nodes: newNodes, edges: newEdges, stats: newStats } = buildTopology(edgeNodes, events, agentToUse);
      
      setNodes(newNodes);
      setEdges(newEdges);
      setStats(newStats);
      
    } catch (err) {
      console.error('Failed to fetch topology data:', err);
      setError(err.message || 'Failed to load network topology');
    } finally {
      setLoading(false);
    }
  }, [buildTopology, setNodes, setEdges, selectedAgent, setSelectedAgent]);

  // Rebuild topology when filter changes
  useEffect(() => {
    if (edgeNodesList.length > 0) {
      const { nodes: newNodes, edges: newEdges, stats: newStats } = buildTopology(edgeNodesList, allEvents, selectedAgent);
      setNodes(newNodes);
      setEdges(newEdges);
      setStats(newStats);
    }
  }, [selectedAgent, edgeNodesList, allEvents, buildTopology, setNodes, setEdges]);

  // Initial fetch and refresh interval
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 180000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Handle node click
  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
  }, []);

  return (
    <div className="h-[calc(100vh-64px)] w-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-40 bg-gradient-to-b from-slate-900/95 to-transparent pb-8 px-6 pt-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-10 h-10 bg-gradient-to-br from-cyan-500/30 to-blue-600/30 rounded-xl flex items-center justify-center border border-cyan-400/30">
                <svg className="w-6 h-6 text-cyan-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
              </span>
              Network Topology
            </h1>
            <p className="text-slate-300 text-sm mt-1 ml-13">Real-time OT infrastructure • Auto-discovered from events</p>
            
            {/* Edge Node Filter Dropdown */}
            <div className="mt-3">
              <EdgeNodeFilter 
                edgeNodes={edgeNodesList}
                selectedAgent={selectedAgent}
                onSelect={setSelectedAgent}
              />
            </div>
          </div>
          <StatsBar stats={stats} />
        </div>
      </div>
      
      {/* Loading state */}
      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/90 backdrop-blur-sm">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin mx-auto mb-4" />
            <div className="text-cyan-300 text-lg font-medium">Building topology...</div>
            <div className="text-slate-400 text-sm mt-1">Analyzing events and discovering devices</div>
          </div>
        </div>
      )}
      
      {/* Error state */}
      {error && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/90 backdrop-blur-sm">
          <div className="bg-red-500/20 border border-red-400/50 rounded-2xl p-8 max-w-md text-center">
            <div className="w-16 h-16 bg-red-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="text-red-300 text-lg font-medium mb-2">Failed to load topology</div>
            <div className="text-slate-300 text-sm">{error}</div>
          </div>
        </div>
      )}
      
      {/* React Flow canvas */}
      {!loading && !error && (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.4, includeHiddenNodes: false }}
          minZoom={0.2}
          maxZoom={1.5}
          defaultViewport={{ x: 0, y: 0, zoom: 0.7 }}
          proOptions={{ hideAttribution: true }}
        >
          <Background 
            variant="dots" 
            gap={20} 
            size={1} 
            color="rgba(6, 182, 212, 0.2)" 
          />
          <Controls 
            className="!bg-slate-700/90 !border-slate-600/50 !rounded-xl overflow-hidden [&>button]:!bg-slate-700 [&>button]:!border-slate-600/50 [&>button]:!text-slate-300 [&>button:hover]:!bg-slate-600 [&>button:hover]:!text-white"
          />
          <MiniMap 
            nodeColor={(node) => {
              if (node.type === 'cloud') return '#22d3ee';
              if (node.type === 'edgeGateway') return node.data?.status === 'Online' ? '#34d399' : '#f87171';
              return '#60a5fa';
            }}
            maskColor="rgba(15, 23, 42, 0.8)"
            className="!bg-slate-800/90 !border-slate-600/50 rounded-xl"
          />
          <Panel position="bottom-left">
            <Legend />
          </Panel>
        </ReactFlow>
      )}
      
      {/* Detail panel */}
      <DetailPanel 
        selectedNode={selectedNode} 
        onClose={() => setSelectedNode(null)} 
      />
    </div>
  );
};

export default NetworkTopology;
